// Busca por cor (spec §4.2 e §7.4): `cor:azul` ou `cor:#1e3a8a` casa quando alguma cor da paleta (peso ≥ 0,05) fica a
// ΔE ≤ 20 de um tom de referência. ΔE CIE76 em Lab (sRGB D65), a mesma conta do servidor em src/search/similar.ts.
// ponytail: CIE76 exagera a distância nos azuis e roxos saturados; por isso cada nome tem vários tons. CIEDE2000 se faltar.

export type Lab = readonly [number, number, number];
export const COLOR_THRESHOLD = 20;
export const MIN_WEIGHT = 0.05;

/** Nome → tons de referência. O primeiro é a amostra mostrada no chip. */
export const COLORS: Record<string, string[]> = {
  vermelho: ["#dc2626", "#ff0000", "#ef4444", "#b91c1c", "#991b1b", "#800000"],
  laranja: ["#f97316", "#ffa500", "#ea580c", "#fb923c", "#c2410c"],
  amarelo: ["#facc15", "#ffff00", "#fde047", "#eab308", "#ffd700"],
  verde: ["#22c55e", "#008000", "#00ff00", "#16a34a", "#166534", "#86efac", "#65a30d"],
  ciano: ["#06b6d4", "#00ffff", "#14b8a6", "#0e7490", "#67e8f9", "#40e0d0"],
  azul: ["#2563eb", "#0000ff", "#3b82f6", "#1d4ed8", "#1e3a8a", "#000080", "#60a5fa", "#93c5fd", "#0ea5e9"],
  roxo: ["#7c3aed", "#800080", "#a855f7", "#6d28d9", "#581c87", "#c084fc"],
  rosa: ["#ec4899", "#ffc0cb", "#ff00ff", "#db2777", "#f472b6", "#f9a8d4"],
  marrom: ["#92400e", "#8b4513", "#78350f", "#7c5a3a", "#a0522d"],
  bege: ["#e7d8c0", "#f5f5dc", "#d6c6a5"],
  preto: ["#000000", "#18181b"],
  branco: ["#ffffff", "#f4f4f5"],
  cinza: ["#808080", "#71717a", "#a1a1aa", "#d4d4d8", "#3f3f46"],
};
// Sinônimos (já dobrados: sem acento, minúsculas).
const ALIAS: Record<string, string> = {
  red: "vermelho", orange: "laranja", yellow: "amarelo", green: "verde", cyan: "ciano", turquesa: "ciano", turquoise: "ciano",
  teal: "ciano", blue: "azul", purple: "roxo", violeta: "roxo", violet: "roxo", lilas: "roxo", pink: "rosa", brown: "marrom",
  beige: "bege", black: "preto", white: "branco", gray: "cinza", grey: "cinza",
};

const HEX = /^#(?:[0-9a-f]{3}){1,2}$/i;

/** Valor de `cor:` já dobrado → "azul" (nome canônico) ou "#rrggbb"; null se não for cor (ex.: ainda digitando). */
export function colorValue(v: string): string | null {
  if (Object.hasOwn(COLORS, v)) return v;
  if (Object.hasOwn(ALIAS, v)) return ALIAS[v];
  if (!HEX.test(v)) return null;
  return v.length === 4 ? "#" + [...v.slice(1)].map((c) => c + c).join("") : v.toLowerCase();
}

export function toLab(hex: string): Lab {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92;
  });
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116);
  const x = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
  const y = f(0.2126 * r + 0.7152 * g + 0.0722 * b);
  const z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

export const deltaE = (a: Lab, b: Lab) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

const NAMED = Object.entries(COLORS).flatMap(([name, hexes]) => hexes.map((h) => [name, toLab(h)] as const));
const refs = new Map<string, Lab[]>(Object.entries(COLORS).map(([name, hexes]) => [name, hexes.map(toLab)]));

/** Tons de referência de um valor devolvido por colorValue(). */
export const refsOf = (value: string): Lab[] => refs.get(value) ?? [toLab(value)];

/** Nome da cor de referência mais próxima (chips de cor da busca). */
export function nearestName(c: Lab): string {
  let [best, min] = ["", Infinity];
  for (const [name, ref] of NAMED) {
    const d = deltaE(c, ref);
    if (d < min) [best, min] = [name, d];
  }
  return best;
}

/** Cores da paleta que contam para a busca (peso ≥ 0,05); sem paleta, a dominante. */
export function paletteLabs(palette: Record<string, number> | null, dominant: string | null): Lab[] {
  const hexes = palette ? Object.keys(palette).filter((h) => palette[h] >= MIN_WEIGHT) : dominant ? [dominant] : [];
  return hexes.filter((h) => HEX.test(h) && h.length === 7).map(toLab);
}
